/**
 * PerformanceBarContainer - Logic and state management for performance bar
 *
 * Manages hover interactions and callbacks for performance bar display.
 * @remarks This is a Container component - contains logic and callbacks
 */

import { memo, useCallback } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import type { MatchStatus } from "@/features/player-detail/types";
import PerformanceBarView from "./PerformanceBarView";

/**
 * Performance data with formatted date for display
 */
interface DisplayPerformance {
  readonly id: string;
  readonly matchDate: string;
  readonly formattedDate: string;
  readonly opponent: string;
  readonly status: MatchStatus;
  readonly rating: number;
  readonly minutes: number;
}

/**
 * Props for PerformanceBarContainer component
 */
interface PerformanceBarProps {
  readonly perf: DisplayPerformance;
  readonly isHovered: boolean;
  /** Whether any bar in the chart is currently hovered/selected */
  readonly isAnyBarHovered: boolean;
  readonly isDark: boolean;
  /** Whether the screen is mobile sized — shows persistent rating labels */
  readonly isSmallScreen: boolean;
  readonly index: number;
  readonly onHoverChange: (index: number | null) => void;
  /** Handler for mobile bar tap — toggles tooltip */
  readonly onPressBar: (index: number) => void;
}

/**
 * PerformanceBarContainer manages hover state for performance bars
 * @param props - Container props
 * @returns PerformanceBarView component
 */
const PerformanceBarContainer = memo(
  ({
    perf,
    isHovered,
    isAnyBarHovered,
    isDark,
    isSmallScreen,
    index,
    onHoverChange,
    onPressBar,
  }: PerformanceBarProps) => {
    const handlePointerEnter = useCallback(() => {
      onHoverChange(index);
    }, [onHoverChange, index]);

    const handlePointerLeave = useCallback(() => {
      onHoverChange(null);
    }, [onHoverChange]);

    const handlePress = useCallback(() => {
      onPressBar(index);
    }, [onPressBar, index]);

    const barTestID = `${TEST_ID_PLAYER_DETAIL.PERFORMANCE_BAR_PREFIX}-${index}`;

    const view = (
      <PerformanceBarView
        perf={perf}
        isHovered={isHovered}
        isAnyBarHovered={isAnyBarHovered}
        isDark={isDark}
        isSmallScreen={isSmallScreen}
        testID={isSmallScreen ? undefined : barTestID}
        onPointerEnter={isSmallScreen ? undefined : handlePointerEnter}
        onPointerLeave={isSmallScreen ? undefined : handlePointerLeave}
      />
    );

    return isSmallScreen ? (
      <Pressable
        testID={barTestID}
        onPress={handlePress}
        className="h-full flex-1"
        accessibilityRole="button"
        accessibilityLabel={`${perf.opponent} - Rating ${perf.rating.toFixed(1)}`}
      >
        {view}
      </Pressable>
    ) : (
      view
    );
  }
);

PerformanceBarContainer.displayName = "PerformanceBarContainer";

export default PerformanceBarContainer;
